import { Injectable } from '@angular/core';
import { LocalStorageService } from '@app/core/services/local-storage.service';
import { UserService } from '@app/core/services/user.service';
import * as SearchActions from '@app/pcs/actions/dossier-search';
import { DossierSearchActionTypes } from '@app/pcs/actions/dossier-search';
import { SearchDossiersByFilter, SelectFilter } from '@app/pcs/actions/filter';
import { InitGridDataPayload, InitSearchGridWithData } from '@app/pcs/actions/init-grid';
import { SearchDossiersByLabels } from '@app/pcs/actions/label';
import { SelectRecentActivity } from '@app/pcs/actions/recent-activity';
import { SetUserGridConfiguration } from '@app/pcs/actions/user-grid-configuration';
import { SearchDossiersBySearchTerm } from '@app/pcs/dashboard/actions/quick-search';
import { DossierSearchKey, DossierSearchType, GridType } from '@app/pcs/models';
import { getDisplaySettingsForSelectedFilter } from '@app/pcs/reducer';
import { DossierSearchGridService } from '@app/pcs/services/dossier-search-grid.service';
import { defaultGridDisplaySettings, isLeaveDossierNavigationAction } from '@app/pcs/util';
import * as fromRoot from '@app/reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { fromJson, mapPayload } from '@util';
import { getAllRows, } from 'ngrx-data-grid';
import * as R from 'ramda';
import { of } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

const prepareDossiersToSaveInLocalStorage = R.map(R.pick(['dossierId', 'title']));

const getSearchAction: any = R.cond<any, any>([
  [R.equals(DossierSearchType.FILTER), R.always(new SearchDossiersByFilter())],
  [R.equals(DossierSearchType.LABELS), R.always(new SearchDossiersByLabels())],
  [R.equals(DossierSearchType.QUICK_SEARCH), R.always(new SearchDossiersBySearchTerm())],
  [R.T, R.always(new SelectRecentActivity())],
]);

@Injectable()
export class SearchEffects {
  gridState$ = this.store.pipe(select(fromRoot.getGridState));
  displaySettingsForSelectedFilter$ = this.store.pipe(select(getDisplaySettingsForSelectedFilter));
  sortedGridData$ = this.gridState$.pipe(select(getAllRows, {gridName: GridType.Search}));

  searchDossiers$ = createEffect(() => this.actions$.pipe(
    ofType<SearchActions.SearchDossiers>(DossierSearchActionTypes.SearchDossiers),
    map(R.propOr(null, 'payload')),
    switchMap((searchType) => {
      const defaultFilterId = this.userService.getDefaultFilterId();
      return !searchType && defaultFilterId
        ? of(new SelectFilter(defaultFilterId))
        : of(getSearchAction(searchType));
    })
  ));

  setSearchResults$ = createEffect(() => this.actions$.pipe(
    ofType<SearchActions.SetSearchResults>(DossierSearchActionTypes.SetSearchResults),
    mapPayload(),
    switchMap(({searchResults, dossierSearchType, userGridConfiguration}) => {
      const configuration = fromJson(userGridConfiguration?.configuration);
      const initGridDataPayload: InitGridDataPayload = {
        data: searchResults,
        defaultColumns: this.dossierSearchGridService.getDossierSearchColumns(dossierSearchType),
        configuration
      };
      return [
        this.initDossierSearchGrid(initGridDataPayload, dossierSearchType),
        new SetUserGridConfiguration({userGridType: userGridConfiguration.userGridType, configuration})
      ];
    })
  ));

  setSearchResultsForFilter$ = createEffect(() => this.actions$.pipe(
    ofType<SearchActions.SetSearchResultsForFilter>(DossierSearchActionTypes.SetSearchResultsForFilter),
    mapPayload(),
    withLatestFrom(this.displaySettingsForSelectedFilter$),
    switchMap(([searchResults, displaySettings]) => {
      const initGridDataPayload: InitGridDataPayload = {
        data: searchResults,
        defaultColumns: this.dossierSearchGridService.getDossierSearchColumns(DossierSearchType.FILTER),
        configuration: displaySettings || defaultGridDisplaySettings
      };
      return [this.initDossierSearchGrid(initGridDataPayload, DossierSearchType.FILTER)];
    })
  ));

  initDossiersInLocalStorage$ = createEffect(() => this.actions$.pipe(
    ofType<SearchActions.InitDossiersInLocalStorage>(DossierSearchActionTypes.InitDossiersInLocalStorage),
    withLatestFrom(this.sortedGridData$),
    tap(([_, dossierSearchItems]) => this.localStorageService.write(
      DossierSearchKey.Dashboard, prepareDossiersToSaveInLocalStorage(dossierSearchItems)
    ))
  ), {dispatch: false});

  clearSearchLocalStorage$ = createEffect(() => this.actions$.pipe(
    filter(isLeaveDossierNavigationAction),
    tap(() => this.localStorageService.remove(...R.values(DossierSearchKey)))
  ), {dispatch: false});

  private initDossierSearchGrid = (initGridDataPayload: InitGridDataPayload, dossierSearchType: DossierSearchType) =>
    new InitSearchGridWithData({
      ...initGridDataPayload,
      actions: [new SearchActions.InitDossiersInLocalStorage(), new SearchActions.SearchDossiersSuccess(dossierSearchType)]
    });

  constructor(
    private actions$: Actions,
    private localStorageService: LocalStorageService,
    private dossierSearchGridService: DossierSearchGridService,
    private userService: UserService,
    private store: Store<any>
  ) {
  }
}
